import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import FooterContact from "@/components/FooterContact";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";

export default function Vilkar() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-vilkar-page">
      <SEO
        title="Vilkår for bruk — ClientFlow"
        description="Vilkår for bruk av ClientFlow — norsk bookingsystem og CRM for småbedrifter."
        path="/vilkar"
      />
      <FloatingThemeToggle />
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="legal-back-home">
            <Logo className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Tilbake
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
          Sist oppdatert · 9. februar 2026
        </p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
          Vilkår for bruk
        </h1>
        <p className="text-base text-muted-foreground mb-10 leading-relaxed">
          ClientFlow er for tiden i en lukket demo- og pilotfase. Tilgang gis etter avtale. Ved å bruke tjenesten godtar du vilkårene under.
        </p>

        <section className="space-y-8 text-[15px] leading-relaxed">
          <Block title="1. Om ClientFlow">
            ClientFlow er en bookings- og CRM-tjeneste rettet mot små serviceforetak i Norge. Tjenesten leveres som en lukket pilot. Funksjoner, priser og tilgjengelighet kan endres mens vi utvikler produktet sammen med pilotbedriftene.
          </Block>

          <Block title="2. Tilgang">
            Kontoer opprettes manuelt etter henvendelse til <Mail /> . Det er ikke åpen registrering. Innloggingen er personlig og skal ikke deles.
          </Block>

          <Block title="3. Ditt ansvar som bedrift">
            Som bedriftseier er du ansvarlig for innholdet du legger inn i ClientFlow, inkludert tjenester, åpningstider, kundedata og meldinger som sendes til kundene dine. Du er behandlingsansvarlig for dine kundedata; ClientFlow er databehandler. Se <Link to="/personvern" className="underline underline-offset-4">personvernerklæringen</Link> for detaljer.
          </Block>

          <Block title="4. Tjenestens tilgjengelighet">
            Vi gjør vårt beste for å holde ClientFlow tilgjengelig, men i pilotfasen gir vi ingen garantert oppetid eller responstid. Planlagt vedlikehold og kortere avbrudd kan forekomme.
          </Block>

          <Block title="5. Betaling">
            I pilotfasen er bruken kostnadsfri og begrenset (Demo-plan). Betalte planer (Basic / Pro) åpnes etter avtale når pilotperioden avsluttes. Priser oppgis i NOK eks. mva.
          </Block>

          <Block title="6. Oppsigelse">
            Du kan be om at kontoen din slettes når som helst ved å sende e-post til <Mail />. Vi kan også avslutte tilgangen din dersom tjenesten misbrukes eller brukes i strid med norsk lov.
          </Block>

          <Block title="7. Ansvarsbegrensning">
            ClientFlow leveres «som det er» under pilotperioden. Vi er ikke ansvarlige for indirekte tap, tapt fortjeneste eller tap av data utover det som følger av ufravikelig norsk lovgivning. Du har selv ansvar for å ta vare på viktige forretningsdata.
          </Block>

          <Block title="8. Endringer">
            Vi kan oppdatere disse vilkårene mens produktet utvikles. Vesentlige endringer varsles på e-post til registrert kontoinnehaver minst 14 dager før de trer i kraft.
          </Block>

          <Block title="9. Kontakt">
            Spørsmål om vilkårene? Send oss en e-post på <Mail />.
          </Block>
        </section>
      </main>

      <footer className="border-t border-border py-8 mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© 2026 ClientFlow</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/vilkar" className="hover:text-foreground">Vilkår</Link>
            <Link to="/personvern" className="hover:text-foreground">Personvern</Link>
            <FooterContact />
          </div>
        </div>
      </footer>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      <div className="text-muted-foreground">{children}</div>
    </div>
  );
}

function Mail() {
  return (
    <a
      href="mailto:hei@clientflow.no"
      className="text-foreground underline underline-offset-4 hover:no-underline"
      data-testid="legal-contact-email"
    >
      hei@clientflow.no
    </a>
  );
}
